import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import BatchSelector, { BatchOption } from '../MarksRecorder/parts/BatchSelector';
import AddAssignmentModal from './parts/AddAssignmentModal';

/* ─── Design Tokens (matches NoticeBoard.tsx) ─────────────────────────── */
const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  navyHover: 'rgba(14,34,93,0.06)',
  gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  red: '#dc2626',
  redDim: 'rgba(220,38,38,0.08)',
  redBorder: 'rgba(220,38,38,0.25)',
};

interface Assignment {
  assignment_id: string;
  institute_id: string;
  branch_id: string | null;
  branch_name: string | null;
  batch_id: string;
  batch_name: string | null;
  assignment_title: string;
  assignment_body: string;
  notes: string;
  files: { file_name: string; url: string; size: number }[];
  start_date: string | null;
  start_time: string | null;
  submission_date: string | null;
  submission_time: string | null;
  allow_late_submissions: boolean;
  created_at: string;
  created_by: string;
  created_by_name: string | null;
}

interface BatchesApiResponse {
  success: boolean;
  data: BatchOption[];
}

interface AssignmentsApiResponse {
  success: boolean;
  data: Assignment[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

const AssignmentBoard: React.FC = () => {
  const { profile } = useOperatorProfileStore();
  const navigate = useNavigate();

  const [batches, setBatches] = useState<BatchOption[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [batchesError, setBatchesError] = useState<string | null>(null);

  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [selectedAssignments, setSelectedAssignments] = useState<Assignment[] | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);

  // Fetch batches — mirrors DocumentGenerator.tsx: plain /v1/batches,
  // filtered to active ones client-side. There is no /v1/batches/by-branch
  // route on the backend.
  useEffect(() => {
    const fetchBatches = async () => {
      try {
        setBatchesLoading(true);
        setBatchesError(null);
        const response = await apiClient.get<BatchesApiResponse>('/v1/batches');
        if (response.data.success) {
          const activeBatches = (response.data.data as any[]).filter((b: any) => b.is_active);
          setBatches(activeBatches);
        } else {
          setBatchesError('Failed to load batches');
        }
      } catch (err: any) {
        setBatchesError(err.response?.data?.error || 'Failed to load batches');
      } finally {
        setBatchesLoading(false);
      }
    };

    fetchBatches();
  }, []);

  const fetchAssignments = useCallback(async () => {
    if (!selectedBatchId) return;

    try {
      setAssignmentsLoading(true);
      setAssignmentsError(null);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await apiClient.get<AssignmentsApiResponse>(
        `/v1/assignments/by-batch/${selectedBatchId}?${params}`
      );
      if (response.data.success) {
        setAssignments(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setAssignmentsError('Failed to load assignments');
      }
    } catch (err: any) {
      setAssignmentsError(err.response?.data?.error || 'Failed to load assignments');
    } finally {
      setAssignmentsLoading(false);
    }
  }, [selectedBatchId, page, limit]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleSelectBatch = (batchId: string) => {
    setSelectedBatchId(batchId);
    setPage(1);
    setSelectedAssignments(null);
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;
    try {
      const response = await apiClient.delete(`/v1/assignments/${assignmentId}`);
      if (response.data.success) {
        fetchAssignments();
      } else {
        setAssignmentsError('Failed to delete assignment');
      }
    } catch (err: any) {
      setAssignmentsError(err.response?.data?.error || 'Failed to delete assignment');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedAssignments || selectedAssignments.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedAssignments.length} assignment(s)?`)) return;

    try {
      await Promise.all(
        selectedAssignments.map(a => apiClient.delete(`/v1/assignments/${a.assignment_id}`))
      );
      setSelectedAssignments(null);
      fetchAssignments();
    } catch (err: any) {
      setAssignmentsError(err.response?.data?.error || 'Failed to delete assignments');
    }
  };

  const handleAddClick = () => {
    setEditingAssignment(null);
    setShowAddModal(true);
  };

  const handleEditClick = (assignment: Assignment) => {
    setEditingAssignment(assignment);
    setShowAddModal(true);
  };

  // Navigates to the AssignmentDetails page — mirrors handleViewDetails
  // pattern used for batches (Batches.tsx "View Details" -> BatchDetailsPage).
  const handleViewClick = (assignmentId: string) => {
    navigate(`/assignments/${assignmentId}/details`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /* ── Column templates (mirror NoticeBoard.tsx templates) ────────────── */

  const titleTemplate = (row: Assignment) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: '600', color: C.navy, fontSize: '0.84rem' }}>{row.assignment_title}</span>
      {row.allow_late_submissions && (
        <span style={{
          padding: '2px 8px',
          borderRadius: '20px',
          fontSize: '0.63rem',
          fontWeight: '700',
          background: C.goldDim,
          color: '#b8860b',
          border: `1px solid ${C.goldBorder}`,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
        }}>
          Late allowed
        </span>
      )}
    </div>
  );

  const notesTemplate = (row: Assignment) => {
    const maxLength = 60;
    const body = row.notes || row.assignment_body || '—';
    return (
      <div style={{
        maxWidth: '280px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: C.sub,
        fontSize: '0.83rem',
      }}>
        {body.length > maxLength ? `${body.substring(0, maxLength)}...` : body}
      </div>
    );
  };

  const startDateTemplate = (row: Assignment) => (
    <span style={{ color: C.sub, fontSize: '0.83rem' }}>{formatDate(row.start_date)}</span>
  );

  const submissionDateTemplate = (row: Assignment) => (
    <span style={{ color: C.sub, fontSize: '0.83rem' }}>{formatDate(row.submission_date)}</span>
  );

  const filesTemplate = (row: Assignment) => (
    <span style={{ color: C.sub, fontSize: '0.83rem' }}>
      {row.files.length > 0 ? (
        <><i className="pi pi-paperclip" style={{ fontSize: '0.7rem', marginRight: '4px' }} />{row.files.length}</>
      ) : '—'}
    </span>
  );

  const actionBodyTemplate = (row: Assignment) => (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
      {[
        {
          icon: 'pi pi-eye',
          title: 'View',
          hover: { bg: C.navyHover, border: 'rgba(14,34,93,0.2)', color: C.navy },
          action: () => handleViewClick(row.assignment_id),
        },
        {
          icon: 'pi pi-pencil',
          title: 'Edit',
          hover: { bg: C.goldDim, border: C.goldBorder, color: '#b8860b' },
          action: () => handleEditClick(row),
        },
        {
          icon: 'pi pi-trash',
          title: 'Delete',
          hover: { bg: C.redDim, border: C.redBorder, color: C.red },
          action: () => handleDeleteAssignment(row.assignment_id),
        },
      ].map(({ icon, title, hover, action }) => (
        <button
          key={icon}
          onClick={action}
          title={title}
          style={{
            width: '30px',
            height: '30px',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: '7px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.muted,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hover.bg;
            e.currentTarget.style.borderColor = hover.border;
            e.currentTarget.style.color = hover.color;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = C.border;
            e.currentTarget.style.color = C.muted;
          }}
        >
          <i className={icon} style={{ fontSize: '0.78rem' }} />
        </button>
      ))}
    </div>
  );

  const renderPagination = () => {
    const pages: (number | string)[] = [];
    const total = pagination.pages;

    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (page <= 3) {
      pages.push(1, 2, 3, '...', total);
    } else if (page >= total - 2) {
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      pages.push(1, '...', page, '...', total);
    }

    return pages.map((p, i) =>
      p === '...' ? (
        <span key={`e${i}`} style={{ padding: '0 4px', color: C.muted, fontSize: '0.82rem' }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => setPage(p as number)}
          style={{
            minWidth: '32px',
            height: '32px',
            padding: '0 8px',
            borderRadius: '7px',
            fontSize: '0.82rem',
            fontWeight: page === p ? '700' : '500',
            background: page === p ? C.navy : 'transparent',
            color: page === p ? C.gold : C.navy,
            border: page === p ? 'none' : `1px solid ${C.border}`,
            boxShadow: page === p ? '0 2px 6px rgba(14,34,93,0.25)' : 'none',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => { if (page !== p) e.currentTarget.style.background = C.navyHover; }}
          onMouseLeave={(e) => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
        >
          {p}
        </button>
      )
    );
  };

  if (batchesLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
      </div>
    );
  }

  const selected = selectedAssignments ?? [];

  return (
    <>
      <style>{`
        .ab-datatable .p-datatable-thead > tr > th {
          background: #fafafa !important;
          color: ${C.muted} !important;
          font-size: 0.7rem !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          border-bottom: 2px solid ${C.border} !important;
          padding: 12px 14px !important;
        }
        .ab-datatable .p-datatable-tbody > tr { transition: background 0.12s; }
        .ab-datatable .p-datatable-tbody > tr:hover > td { background: rgba(14,34,93,0.025) !important; }
        .ab-datatable .p-datatable-tbody > tr > td {
          border-bottom: 1px solid #f3f4f6 !important;
          padding: 10px 12px !important;
          font-size: 0.83rem;
        }
        .ab-datatable .p-checkbox .p-checkbox-box.p-highlight { background: ${C.navy} !important; border-color: ${C.navy} !important; }
        .ab-datatable .p-checkbox .p-checkbox-box:hover { border-color: ${C.gold} !important; }
        .ab-datatable .p-datatable-loading-overlay { background: rgba(255,255,255,0.7) !important; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {batchesError && <Message severity="error" text={batchesError} style={{ width: '100%', fontSize: '0.88rem' }} />}

        <BatchSelector
          batches={batches}
          selectedBatchId={selectedBatchId}
          onSelect={handleSelectBatch}
        />

        {!selectedBatchId && (
          <div style={{
            background: C.white,
            borderRadius: '14px',
            border: `1px solid ${C.border}`,
            padding: '48px 20px',
            textAlign: 'center',
          }}>
            <i className="pi pi-book" style={{ fontSize: '2rem', color: C.muted, opacity: 0.4, display: 'block', marginBottom: '10px' }} />
            <p style={{ margin: 0, color: C.muted, fontSize: '0.9rem' }}>Select a batch to view its assignments</p>
          </div>
        )}

        {selectedBatchId && (
          <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(14,34,93,0.06)', overflow: 'hidden' }}>

            {/* Table Header Row — mirrors NoticeBoard.tsx */}
            <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.8rem', color: C.sub }}>Showing per page</span>
                <select
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  style={{
                    padding: '4px 8px',
                    border: `1px solid ${C.border}`,
                    borderRadius: '7px',
                    fontSize: '0.8rem',
                    color: C.navy,
                    background: C.white,
                    fontWeight: '600',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>

              <button
                onClick={handleAddClick}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  padding: '10px 18px',
                  background: C.navy,
                  border: 'none',
                  borderRadius: '9px',
                  color: C.white,
                  fontSize: '0.82rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.navyMid; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <i className="pi pi-plus" style={{ fontSize: '0.82rem' }} />
                Add Assignment
              </button>

              {selected.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', background: '#f8f9ff', border: `1px solid ${C.border}`, borderRadius: '10px' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: '700', color: C.navy }}>{selected.length} Selected</span>
                  <button
                    onClick={handleBulkDelete}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 12px',
                      background: C.redDim,
                      border: `1px solid ${C.redBorder}`,
                      borderRadius: '7px',
                      color: C.red,
                      fontSize: '0.78rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                    }}
                  >
                    <i className="pi pi-trash" style={{ fontSize: '0.72rem' }} />
                    Bulk Delete
                  </button>
                </div>
              )}
            </div>

            {assignmentsError && (
              <div style={{ padding: '14px 18px' }}>
                <Message severity="error" text={assignmentsError} style={{ width: '100%', fontSize: '0.88rem' }} />
              </div>
            )}

            {/* DataTable — mirrors NoticeBoard.tsx */}
            <DataTable
              value={assignments}
              selection={selectedAssignments}
              onSelectionChange={(e: any) => setSelectedAssignments(e.value)}
              dataKey="assignment_id"
              loading={assignmentsLoading}
              emptyMessage={
                <div style={{ textAlign: 'center', padding: '40px 20px', color: C.muted }}>
                  <i className="pi pi-file-edit" style={{ fontSize: '2rem', display: 'block', marginBottom: '10px', opacity: 0.4 }} />
                  <span style={{ fontSize: '0.9rem' }}>No assignments found for this batch</span>
                </div>
              }
              className="ab-datatable"
              style={{ fontSize: '0.83rem' }}
            >
              <Column selectionMode="multiple" headerStyle={{ width: '3rem', padding: '12px 14px' }} bodyStyle={{ padding: '12px 14px' }} />
              <Column field="assignment_title" header="Assignment Title" headerStyle={{ minWidth: '260px' }} body={titleTemplate} />
              <Column field="notes" header="Notes" body={notesTemplate} />
              <Column field="start_date" header="Start Date" body={startDateTemplate} headerStyle={{ minWidth: '130px' }} />
              <Column field="submission_date" header="Submit By" body={submissionDateTemplate} headerStyle={{ minWidth: '130px' }} />
              <Column field="files" header="Files" body={filesTemplate} headerStyle={{ minWidth: '90px' }} />
              <Column header="Actions" body={actionBodyTemplate} headerStyle={{ width: '120px', textAlign: 'center' }} bodyStyle={{ textAlign: 'center' }} />
            </DataTable>

            {/* Pagination Footer — mirrors NoticeBoard.tsx */}
            {pagination.pages > 0 && (
              <div style={{ padding: '14px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <span style={{ fontSize: '0.8rem', color: C.muted }}>
                  Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total} assignments
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                  >
                    <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                  >
                    <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }} />
                  </button>

                  {renderPagination()}

                  <button
                    onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={page === pagination.pages}
                    style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                  >
                    <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }} />
                  </button>
                  <button
                    onClick={() => setPage(pagination.pages)}
                    disabled={page === pagination.pages}
                    style={{ width: '30px', height: '30px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', cursor: page === pagination.pages ? 'not-allowed' : 'pointer', opacity: page === pagination.pages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.navy }}
                  >
                    <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {profile?.institute_id && profile?.branch_id && (
          <AddAssignmentModal
            visible={showAddModal}
            instituteId={profile.institute_id}
            branchId={profile.branch_id}
            editingAssignment={editingAssignment}
            onHide={() => { setShowAddModal(false); setEditingAssignment(null); }}
            onSuccess={() => { fetchAssignments(); }}
          />
        )}
      </div>
    </>
  );
};

export default AssignmentBoard;